// P5.3 — Profile-specific Level 3 block configs.
//
// One config per `profile_key`. Matches 1-to-1 the Pydantic models in
// `backend/app/models_user_settings.py` (`PROFILE_BLOCK_MODELS`).
// Each entry is the `fields` array that SimpleSection renders for the
// active profile block.
//
// HIDDEN profiles (`is_public: false` in the registry):
//   • brand_sponsor
//   • head_coach
//   • tournament_organizer
// These are listed here so users who already own that profile_key
// (assigned by an admin) get a working Level 3 form, BUT they are
// never offered as a selectable option in the public ActiveProfile
// switcher (see `ActiveProfileSelector.jsx`).
//
// Field `kind` values supported by SimpleSection:
//   • "text"      → free text (default)
//   • "textarea"  → multi-line free text
//   • "bool"      → checkbox toggle
//   • "csv"       → comma-separated → array<string>
//   • "select"    → closed enum dropdown; provide `options: [{value, labelKey}]`
//   • "brand_multiselect" / "category_multiselect" / "sponsorship_category_multiselect"
//
// Phase 1 — Profile-Specific Data Integration (Feb 2026):
//   - Added `sports_doctor`, `sport_psychologist`, `tournament_organizer` configs.
//   - Mirrored `Literal[…]` enums from backend as `kind: "select"` to remove
//     422 risk and the confusion of "type the exact slug" UX.

export const PROFILE_BLOCK_FIELDS = {
  player: [
    { key: "date_of_birth",                  labelKey: "profileBlock.player.date_of_birth" },
    { key: "gender",                         labelKey: "profileBlock.player.gender" },
    { key: "competitive_level",              labelKey: "profileBlock.player.competitive_level" },
    { key: "ranking_category",               labelKey: "profileBlock.player.ranking_category" },
    { key: "primary_discipline",             labelKey: "profileBlock.player.primary_discipline" },
    { key: "current_club_academy",           labelKey: "profileBlock.player.current_club_academy" },
    { key: "training_volume_per_week_hours", labelKey: "profileBlock.player.training_volume_per_week_hours" },
    { key: "competition_frequency",          labelKey: "profileBlock.player.competition_frequency", kind: "select",
      options: [
        { value: "never",               labelKey: "profileBlock.enums.competition_frequency.never" },
        { value: "monthly",             labelKey: "profileBlock.enums.competition_frequency.monthly" },
        { value: "weekly",              labelKey: "profileBlock.enums.competition_frequency.weekly" },
        { value: "multiple_per_week",   labelKey: "profileBlock.enums.competition_frequency.multiple_per_week" },
      ] },
    { key: "support_team_available",         labelKey: "profileBlock.player.support_team_available", kind: "csv" },
  ],
  coach: [
    { key: "specialization",            labelKey: "profileBlock.coach.specialization" },
    { key: "years_experience",          labelKey: "profileBlock.coach.years_experience" },
    { key: "certification_level",       labelKey: "profileBlock.coach.certification_level" },
    { key: "players_type",              labelKey: "profileBlock.coach.players_type" },
    { key: "players_managed_count",     labelKey: "profileBlock.coach.players_managed_count" },
    { key: "work_setting",              labelKey: "profileBlock.coach.work_setting", kind: "select",
      options: [
        { value: "club",       labelKey: "profileBlock.enums.coach_work_setting.club" },
        { value: "academy",    labelKey: "profileBlock.enums.coach_work_setting.academy" },
        { value: "federation", labelKey: "profileBlock.enums.coach_work_setting.federation" },
        { value: "private",    labelKey: "profileBlock.enums.coach_work_setting.private" },
        { value: "school",     labelKey: "profileBlock.enums.coach_work_setting.school" },
        { value: "other",      labelKey: "profileBlock.enums.coach_work_setting.other" },
      ] },
    { key: "digital_tools_in_coaching", labelKey: "profileBlock.coach.digital_tools_in_coaching", kind: "csv" },
  ],
  club_director: [
    { key: "organization_size",             labelKey: "profileBlock.club_director.organization_size", kind: "select",
      options: [
        { value: "xs", labelKey: "profileBlock.enums.size.xs" },
        { value: "s",  labelKey: "profileBlock.enums.size.s" },
        { value: "m",  labelKey: "profileBlock.enums.size.m" },
        { value: "l",  labelKey: "profileBlock.enums.size.l" },
        { value: "xl", labelKey: "profileBlock.enums.size.xl" },
      ] },
    { key: "athletes_count",                labelKey: "profileBlock.club_director.athletes_count" },
    { key: "coaches_count",                 labelKey: "profileBlock.club_director.coaches_count" },
    { key: "locations_count",               labelKey: "profileBlock.club_director.locations_count" },
    { key: "sports_offered",                labelKey: "profileBlock.club_director.sports_offered", kind: "csv" },
    { key: "management_model",              labelKey: "profileBlock.club_director.management_model", kind: "select",
      options: [
        { value: "private",    labelKey: "profileBlock.enums.mgmt_model.private" },
        { value: "public",     labelKey: "profileBlock.enums.mgmt_model.public" },
        { value: "mixed",      labelKey: "profileBlock.enums.mgmt_model.mixed" },
        { value: "non_profit", labelKey: "profileBlock.enums.mgmt_model.non_profit" },
      ] },
    { key: "current_software_systems",      labelKey: "profileBlock.club_director.current_software_systems", kind: "csv" },
    { key: "primary_operational_challenge", labelKey: "profileBlock.club_director.primary_operational_challenge", kind: "textarea" },
  ],
  federation_manager: [
    { key: "institution_type",         labelKey: "profileBlock.federation_manager.institution_type", kind: "select",
      options: [
        { value: "national",      labelKey: "profileBlock.enums.institution_type.national" },
        { value: "regional",      labelKey: "profileBlock.enums.institution_type.regional" },
        { value: "local",         labelKey: "profileBlock.enums.institution_type.local" },
        { value: "international", labelKey: "profileBlock.enums.institution_type.international" },
      ] },
    { key: "jurisdiction",             labelKey: "profileBlock.federation_manager.jurisdiction" },
    { key: "programs_managed",         labelKey: "profileBlock.federation_manager.programs_managed", kind: "csv" },
    { key: "estimated_athlete_base",   labelKey: "profileBlock.federation_manager.estimated_athlete_base" },
    { key: "estimated_coach_network",  labelKey: "profileBlock.federation_manager.estimated_coach_network" },
    { key: "funding_model",            labelKey: "profileBlock.federation_manager.funding_model", kind: "select",
      options: [
        { value: "public",  labelKey: "profileBlock.enums.funding_model.public" },
        { value: "private", labelKey: "profileBlock.enums.funding_model.private" },
        { value: "mixed",   labelKey: "profileBlock.enums.funding_model.mixed" },
      ] },
    { key: "strategic_priority_area",  labelKey: "profileBlock.federation_manager.strategic_priority_area", kind: "textarea" },
  ],
  parent_guardian: [
    { key: "relationship_to_athlete",     labelKey: "profileBlock.parent_guardian.relationship_to_athlete", kind: "select",
      options: [
        { value: "parent",         labelKey: "profileBlock.enums.relationship.parent" },
        { value: "legal_guardian", labelKey: "profileBlock.enums.relationship.legal_guardian" },
        { value: "family",         labelKey: "profileBlock.enums.relationship.family" },
      ] },
    { key: "athlete_age",                 labelKey: "profileBlock.parent_guardian.athlete_age" },
    { key: "athlete_level",               labelKey: "profileBlock.parent_guardian.athlete_level" },
    { key: "main_current_concern",        labelKey: "profileBlock.parent_guardian.main_current_concern", kind: "textarea" },
    { key: "monthly_spending_band",       labelKey: "profileBlock.parent_guardian.monthly_spending_band", kind: "select",
      options: [
        { value: "under_100",  labelKey: "profileBlock.enums.spending.under_100" },
        { value: "100_500",    labelKey: "profileBlock.enums.spending.100_500" },
        { value: "500_1500",   labelKey: "profileBlock.enums.spending.500_1500" },
        { value: "1500_5000",  labelKey: "profileBlock.enums.spending.1500_5000" },
        { value: "over_5000",  labelKey: "profileBlock.enums.spending.over_5000" },
      ] },
    { key: "need_coordination_with_staff",labelKey: "profileBlock.parent_guardian.need_coordination_with_staff", kind: "bool" },
  ],
  fitness_coach: [
    { key: "primary_population_served",     labelKey: "profileBlock.fitness_coach.primary_population_served" },
    { key: "years_experience",              labelKey: "profileBlock.fitness_coach.years_experience" },
    { key: "certifications",                labelKey: "profileBlock.fitness_coach.certifications", kind: "csv" },
    { key: "performance_testing_tools_used",labelKey: "profileBlock.fitness_coach.performance_testing_tools_used", kind: "csv" },
    { key: "athletes_managed_count",        labelKey: "profileBlock.fitness_coach.athletes_managed_count" },
    { key: "need_load_monitoring",          labelKey: "profileBlock.fitness_coach.need_load_monitoring", kind: "bool" },
  ],
  physiotherapist: [
    { key: "practice_setting",       labelKey: "profileBlock.physiotherapist.practice_setting", kind: "select",
      options: [
        { value: "clinic",     labelKey: "profileBlock.enums.medical_practice_setting.clinic" },
        { value: "club",       labelKey: "profileBlock.enums.medical_practice_setting.club" },
        { value: "federation", labelKey: "profileBlock.enums.medical_practice_setting.federation" },
        { value: "private",    labelKey: "profileBlock.enums.medical_practice_setting.private" },
        { value: "hospital",   labelKey: "profileBlock.enums.medical_practice_setting.hospital" },
        { value: "other",      labelKey: "profileBlock.enums.medical_practice_setting.other" },
      ] },
    { key: "years_experience",       labelKey: "profileBlock.physiotherapist.years_experience" },
    { key: "specialization",         labelKey: "profileBlock.physiotherapist.specialization" },
    { key: "cases_type",             labelKey: "profileBlock.physiotherapist.cases_type" },
    { key: "athletes_treated_monthly",labelKey: "profileBlock.physiotherapist.athletes_treated_monthly" },
    { key: "need_injury_tracking",   labelKey: "profileBlock.physiotherapist.need_injury_tracking", kind: "bool" },
  ],
  // HIDDEN — surfaced only if user already owns this profile_key.
  brand_sponsor: [
    { key: "company_name",                    labelKey: "profileBlock.brand_sponsor.company_name" },
    { key: "industry",                        labelKey: "profileBlock.brand_sponsor.industry" },
    { key: "market_scope",                    labelKey: "profileBlock.brand_sponsor.market_scope", kind: "select",
      options: [
        { value: "local",    labelKey: "profileBlock.enums.market_scope.local" },
        { value: "national", labelKey: "profileBlock.enums.market_scope.national" },
        { value: "regional", labelKey: "profileBlock.enums.market_scope.regional" },
        { value: "global",   labelKey: "profileBlock.enums.market_scope.global" },
      ] },
    { key: "role_in_partnerships",            labelKey: "profileBlock.brand_sponsor.role_in_partnerships" },
    { key: "target_audience",                 labelKey: "profileBlock.brand_sponsor.target_audience", kind: "textarea" },
    { key: "current_sports_activation_model", labelKey: "profileBlock.brand_sponsor.current_sports_activation_model", kind: "textarea" },
    { key: "interest_area",                   labelKey: "profileBlock.brand_sponsor.interest_area", kind: "textarea" },
  ],
  racket_technician: [
    { key: "work_setting",                    labelKey: "profileBlock.racket_technician.work_setting", kind: "select",
      options: [
        { value: "shop",       labelKey: "profileBlock.enums.tech_work_setting.shop" },
        { value: "club",       labelKey: "profileBlock.enums.tech_work_setting.club" },
        { value: "academy",    labelKey: "profileBlock.enums.tech_work_setting.academy" },
        { value: "tournament", labelKey: "profileBlock.enums.tech_work_setting.tournament" },
        { value: "freelance",  labelKey: "profileBlock.enums.tech_work_setting.freelance" },
      ] },
    { key: "years_experience",                labelKey: "profileBlock.racket_technician.years_experience" },
    { key: "stringing_machines_used",         labelKey: "profileBlock.racket_technician.stringing_machines_used", kind: "csv" },
    { key: "string_types_offered",            labelKey: "profileBlock.racket_technician.string_types_offered", kind: "csv" },
    { key: "rackets_strung_per_month",        labelKey: "profileBlock.racket_technician.rackets_strung_per_month" },
    { key: "tournament_service_experience",   labelKey: "profileBlock.racket_technician.tournament_service_experience", kind: "bool" },
    { key: "inventory_management_needs",      labelKey: "profileBlock.racket_technician.inventory_management_needs", kind: "csv" },
    { key: "player_preference_tracking_needs",labelKey: "profileBlock.racket_technician.player_preference_tracking_needs", kind: "bool" },
  ],
  // HIDDEN — surfaced only if user already owns this profile_key.
  head_coach: [
    { key: "academy_or_program_supervised",  labelKey: "profileBlock.head_coach.academy_or_program_supervised" },
    { key: "coaches_supervised_count",       labelKey: "profileBlock.head_coach.coaches_supervised_count" },
    { key: "players_supervised_count",       labelKey: "profileBlock.head_coach.players_supervised_count" },
    { key: "methodology_responsibility",     labelKey: "profileBlock.head_coach.methodology_responsibility", kind: "bool" },
    { key: "performance_tracking_responsibility",labelKey: "profileBlock.head_coach.performance_tracking_responsibility", kind: "bool" },
    { key: "coach_evaluation_responsibility",labelKey: "profileBlock.head_coach.coach_evaluation_responsibility", kind: "bool" },
    { key: "program_reporting_needs",        labelKey: "profileBlock.head_coach.program_reporting_needs", kind: "csv" },
    { key: "strategic_development_priorities",labelKey: "profileBlock.head_coach.strategic_development_priorities", kind: "csv" },
  ],
  // Phase 1 — Profile-Specific Data Integration (Feb 2026).
  // Medical-sensitive: backend gates save with `consent_aggregated_research_use`.
  // The consent toggle is always FIRST in the field list so users see it
  // immediately and the MedicalConsentBanner explains the legal context.
  sports_doctor: [
    { key: "consent_aggregated_research_use", labelKey: "profileBlock.sports_doctor.consent_aggregated_research_use", kind: "bool" },
    { key: "specialization", labelKey: "profileBlock.sports_doctor.specialization", kind: "select",
      options: [
        { value: "sports_medicine", labelKey: "profileBlock.enums.doctor_specialization.sports_medicine" },
        { value: "orthopedics",     labelKey: "profileBlock.enums.doctor_specialization.orthopedics" },
        { value: "cardiology",      labelKey: "profileBlock.enums.doctor_specialization.cardiology" },
        { value: "pediatrics",      labelKey: "profileBlock.enums.doctor_specialization.pediatrics" },
        { value: "other",           labelKey: "profileBlock.enums.doctor_specialization.other" },
      ] },
    { key: "practice_setting", labelKey: "profileBlock.sports_doctor.practice_setting", kind: "select",
      options: [
        { value: "clinic",     labelKey: "profileBlock.enums.medical_practice_setting.clinic" },
        { value: "club",       labelKey: "profileBlock.enums.medical_practice_setting.club" },
        { value: "federation", labelKey: "profileBlock.enums.medical_practice_setting.federation" },
        { value: "hospital",   labelKey: "profileBlock.enums.medical_practice_setting.hospital" },
        { value: "private",    labelKey: "profileBlock.enums.medical_practice_setting.private" },
        { value: "other",      labelKey: "profileBlock.enums.medical_practice_setting.other" },
      ] },
    { key: "years_experience",                          labelKey: "profileBlock.sports_doctor.years_experience" },
    { key: "athletes_treated_monthly",                  labelKey: "profileBlock.sports_doctor.athletes_treated_monthly" },
    { key: "team_or_federation_official_affiliation",   labelKey: "profileBlock.sports_doctor.team_or_federation_official_affiliation" },
    { key: "need_athlete_health_record_continuity",     labelKey: "profileBlock.sports_doctor.need_athlete_health_record_continuity", kind: "bool" },
    { key: "interest_in_anti_doping_tools",             labelKey: "profileBlock.sports_doctor.interest_in_anti_doping_tools", kind: "bool" },
  ],
  sport_psychologist: [
    { key: "consent_aggregated_research_use", labelKey: "profileBlock.sport_psychologist.consent_aggregated_research_use", kind: "bool" },
    { key: "specialization", labelKey: "profileBlock.sport_psychologist.specialization", kind: "select",
      options: [
        { value: "performance",     labelKey: "profileBlock.enums.psychology_specialization.performance" },
        { value: "clinical",        labelKey: "profileBlock.enums.psychology_specialization.clinical" },
        { value: "developmental",   labelKey: "profileBlock.enums.psychology_specialization.developmental" },
        { value: "team_dynamics",   labelKey: "profileBlock.enums.psychology_specialization.team_dynamics" },
        { value: "other",           labelKey: "profileBlock.enums.psychology_specialization.other" },
      ] },
    { key: "years_experience", labelKey: "profileBlock.sport_psychologist.years_experience" },
    { key: "practice_setting", labelKey: "profileBlock.sport_psychologist.practice_setting", kind: "select",
      options: [
        { value: "clinic",     labelKey: "profileBlock.enums.medical_practice_setting.clinic" },
        { value: "club",       labelKey: "profileBlock.enums.medical_practice_setting.club" },
        { value: "federation", labelKey: "profileBlock.enums.medical_practice_setting.federation" },
        { value: "academy",    labelKey: "profileBlock.enums.medical_practice_setting.academy" },
        { value: "private",    labelKey: "profileBlock.enums.medical_practice_setting.private" },
        { value: "other",      labelKey: "profileBlock.enums.medical_practice_setting.other" },
      ] },
    { key: "athletes_per_month",                labelKey: "profileBlock.sport_psychologist.athletes_per_month" },
    { key: "intervention_modalities",           labelKey: "profileBlock.sport_psychologist.intervention_modalities", kind: "csv" },
    { key: "need_session_continuity_tools",     labelKey: "profileBlock.sport_psychologist.need_session_continuity_tools", kind: "bool" },
    { key: "interest_in_wellbeing_metrics",     labelKey: "profileBlock.sport_psychologist.interest_in_wellbeing_metrics", kind: "bool" },
  ],
  // HIDDEN — surfaced only if user already owns this profile_key.
  // Backend block is ready; UI ships symmetric for the day Roberto flips
  // `is_public: True` in profiles.py.
  tournament_organizer: [
    { key: "event_scope", labelKey: "profileBlock.tournament_organizer.event_scope", kind: "select",
      options: [
        { value: "local",         labelKey: "profileBlock.enums.event_scope.local" },
        { value: "regional",      labelKey: "profileBlock.enums.event_scope.regional" },
        { value: "national",      labelKey: "profileBlock.enums.event_scope.national" },
        { value: "international", labelKey: "profileBlock.enums.event_scope.international" },
      ] },
    { key: "tournaments_organized_per_year",   labelKey: "profileBlock.tournament_organizer.tournaments_organized_per_year" },
    { key: "discipline_specialization",        labelKey: "profileBlock.tournament_organizer.discipline_specialization", kind: "csv" },
    { key: "participants_band",                labelKey: "profileBlock.tournament_organizer.participants_band", kind: "select",
      options: [
        { value: "under_50",   labelKey: "profileBlock.enums.participants_band.under_50" },
        { value: "50_200",     labelKey: "profileBlock.enums.participants_band.50_200" },
        { value: "200_1000",   labelKey: "profileBlock.enums.participants_band.200_1000" },
        { value: "over_1000",  labelKey: "profileBlock.enums.participants_band.over_1000" },
      ] },
    { key: "prize_money_handling",             labelKey: "profileBlock.tournament_organizer.prize_money_handling", kind: "bool" },
    { key: "live_streaming_needs",             labelKey: "profileBlock.tournament_organizer.live_streaming_needs", kind: "bool" },
    { key: "vendor_partner_management_needs",  labelKey: "profileBlock.tournament_organizer.vendor_partner_management_needs", kind: "bool" },
    { key: "interest_in_team_os_event_tools",  labelKey: "profileBlock.tournament_organizer.interest_in_team_os_event_tools", kind: "bool" },
  ],
};

// Profiles that are NOT publicly listable in the active-profile switcher.
// Mirrors `is_public: false` in `backend/app/profiles.py`.
// Phase A5 (Feb 2026): head_coach flipped to public — removed from this set.
export const HIDDEN_PROFILES = new Set(["brand_sponsor", "tournament_organizer"]);

// Medical-sensitive profiles — backend requires `consent_aggregated_research_use`
// before persisting any substantive field. Mirrors
// `MEDICAL_SENSITIVE_PROFILE_KEYS` in `backend/app/models_user_settings.py`.
// Frontend uses this set to render the MedicalConsentBanner above the L3 form.
export const MEDICAL_SENSITIVE_PROFILES = new Set(["sports_doctor", "sport_psychologist"]);

